"use client";

import Image from "next/image";
import { useLocale } from "@/context/LocaleContext";

export default function AutoShareFeature() {
  const { t } = useLocale(); 

  return (
    <div className="w-full">
      <section className="w-full bg-gray-50 py-16 md:py-14 mt-10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-20">
            <div className="w-full md:w-[55%] flex justify-center">
              <div className="relative w-full max-w-[500px] aspect-[3/4]">
                <Image 
                  src="/images/cta/sebar-otomatis.webp" 
                  alt="Mockup cara kerja wa broadcast" 
                  fill 
                  className="object-contain drop-shadow-2xl" 
                />
              </div>
            </div>

            <div className="w-full md:w-[45%] lg:pr-10">
              <h2 className="text-3xl md:text-4xl lg:text-[2.4rem] font-extrabold text-gray-900 mb-8 leading-tight">
                {t("AutoShare.how_it_works") || "Bagaimana cara kerjanya?"}
              </h2>
              
              <div className="space-y-6 lg:space-y-8">
                {/* Langkah 1 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">1.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("AutoShare.step1_title") || "Siapkan Daftar Tamu"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-lg">
                      {t("AutoShare.step1_desc") || "Masukkan daftar tamu berserta nomor whatsapp mereka ke dalam sistem"}
                    </p>
                  </div>
                </div>

                {/* Langkah 2 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">2.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("AutoShare.step2_title") || "Atur Pesan Undangan"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-lg">
                      {t("AutoShare.step2_desc") || "Tentukan format pesan undangan yang ingin dikirim, lengkap dengan link undangan personal untuk setiap tamu"}
                    </p>
                  </div>
                </div>

                {/* Langkah 3 */}
                <div className="flex items-start gap-4 lg:gap-5">
                  <span className="text-xl md:text-2xl font-black text-gray-900 mt-0.5">3.</span>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-1.5">
                      {t("AutoShare.step3_title") || "Sebar otomatis"}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-[15px] md:text-lg">
                      {t("AutoShare.step3_desc_1") || "Sistem elynk akan mengirim undangan secara bertahap dan terkontrol melalui whatsapp."} 
                    </p>
                    
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed text-[15px] md:text-lg">
                  {t("AutoShare.step3_desc_2") || "Semua proses berjalan tanpa perlu intervensi manual"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24 space-y-6">
            <h2 className="text-4xl md:text-5xl lg:text-[3rem] font-extrabold text-gray-900 tracking-tight">
              {t("AutoShare.benefits_title") || "Apa keunggulannya?"}
            </h2>
            <div className="space-y-3">
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800">
                {t("AutoShare.benefits_subtitle") || "Proses Sebar Undangan Lebih Cepat"}
              </h3>
              <p className="text-gray-700 text-lg md:text-xl leading-relaxed">
                {t("AutoShare.benefits_desc") || "Pengiriman undangan ke ratusan hingga ribuan tamu dapat dilakukan dalam waktu singkat."}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="bg-gray-50 rounded-[40px] p-10 md:p-14 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-20 h-20 mb-8 group-hover:scale-110 transition-transform duration-300 relative">
                <Image 
                  src="/images/icons/mail-send.svg" 
                  alt="Icon minim kesalahan" 
                  fill
                  className="object-contain"
                />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                {t("AutoShare.card1_title") || "Minim Kesalahan Pengiriman"}
              </h4>
              <p className="text-gray-700 text-lg md:text-xl mb-4 font-medium">
                {t("AutoShare.card1_desc") || "Karena proses dilakukan oleh sistem, risiko:"}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base md:text-lg lg:text-xl">
                <li>{t("AutoShare.card1_li_1") || "Salah kirim pesan"}</li>
                <li>{t("AutoShare.card1_li_2") || "Salah link undangan"}</li>
                <li>{t("AutoShare.card1_li_3") || "Lupa mengirim ke tamu tertentu dapat diminimalkan."}</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-[40px] p-10 md:p-14 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-20 h-20 mb-8 group-hover:scale-110 transition-transform duration-300 relative">
                <Image 
                  src="/images/icons/groups.svg" 
                  alt="Icon efisien tamu" 
                  fill
                  className="object-contain"
                />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                {t("AutoShare.card2_title") || "Efisien untuk Banyak Tamu"}
              </h4>
              <p className="text-gray-700 text-lg md:text-xl mb-4 font-medium">
                {t("AutoShare.card2_desc") || "Fitur ini sangat ideal untuk:"}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base md:text-lg lg:text-xl">
                <li>{t("AutoShare.card2_li_1") || "Acara dengan jumlah tamu besar"}</li>
                <li>{t("AutoShare.card2_li_2") || "Event profesional atau corporate"}</li>
                <li>{t("AutoShare.card2_li_3") || "Pengguna yang ingin menghemat waktu dan tenaga"}</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-[40px] p-10 md:p-14 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-20 h-20 mb-8 group-hover:scale-110 transition-transform duration-300 relative">
                <Image 
                  src="/images/icons/note.svg" 
                  alt="Icon terstruktur" 
                  fill
                  className="object-contain"
                />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                {t("AutoShare.card3_title") || "Terstruktur & Tercatat"}
              </h4>
              <p className="text-gray-700 text-lg md:text-xl mb-4 font-medium">
                {t("AutoShare.card3_desc") || "Setiap proses pengiriman undangan tercatat dalam sistem, sehingga kamu dapat:"}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base md:text-lg lg:text-xl">
                <li>{t("AutoShare.card3_li_1") || "Mengetahui undangan yang sudah terkirim"}</li>
                <li>{t("AutoShare.card3_li_2") || "Memantau progres pengiriman"}</li>
                <li>{t("AutoShare.card3_li_3") || "Mengelola daftar tamu dengan lebih rapi"}</li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-[40px] p-10 md:p-14 border border-gray-100 hover:shadow-xl transition-all duration-300 group">
              <div className="w-20 h-20 mb-8 group-hover:scale-110 transition-transform duration-300 relative">
                <Image 
                  src="/images/icons/setting-auto-share.svg" 
                  alt="Icon terintegrasi" 
                  fill
                  className="object-contain"
                />
              </div>
              <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                {t("AutoShare.card4_title") || "Terintegrasi dengan Fitur Lain"}
              </h4>
              <p className="text-gray-700 text-lg md:text-xl mb-5 font-medium">
                {t("AutoShare.card4_desc") || "Sebar otomatis via whatsapp terhubung langsung dengan:"}
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 text-base md:text-lg lg:text-xl mb-6">
                <li>{t("AutoShare.card4_li_1") || "Custom rsvp"}</li>
                <li>{t("AutoShare.card4_li_2") || "Reminder acara otomatis"}</li>
                <li>{t("AutoShare.card4_li_3") || "Laporan statistik sebar"}</li>
              </ul>
              <p className="text-gray-800 text-lg md:text-xl pl-4">
                {t("AutoShare.card4_footer") || "Semua data bekerja saling terhubung untuk mendukung kelancaran acara."}
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}